import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DailyIframe from '@daily-co/daily-js';
import { supabase } from '../supabaseClient';
import { QUICK_NOTE_TAGS, parseEmployerNote, serializeEmployerNote, tagSentiment } from '../utils/noteTags';
import LanguageToggle from '../components/LanguageToggle';
import VocationWorksLogo from '../components/VocationWorksLogo';
import LineIcon from '../components/LineIcon';

const NAVY     = '#0D1B2A';
const NAVY_MID = '#132233';
const AMBER    = '#F5A623';
const WHITE    = '#FFFFFF';
const GREEN    = '#1D9E75';
const RED      = '#E24B4A';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";

// ── Small shared pieces ─────────────────────────────────────
function PanelCard({ title, children }) {
  return (
    <div style={{ backgroundColor: NAVY_MID, borderRadius: '12px', padding: '16px', border: '1px solid rgba(255,255,255,0.07)' }}>
      <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '12px' }}>
        {title}
      </p>
      {children}
    </div>
  );
}

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', padding: '5px 0', fontSize: '0.85rem' }}>
      <span style={{ color: 'rgba(255,255,255,0.45)' }}>{label}</span>
      <span style={{ color: WHITE, fontWeight: 600, textAlign: 'right' }}>{value}</span>
    </div>
  );
}

function Pill({ children }) {
  return (
    <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'rgba(255,255,255,0.7)', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '999px', padding: '3px 10px' }}>
      {children}
    </span>
  );
}

function JobDetailsPanel({ job }) {
  const { t } = useTranslation();
  if (!job) return null;
  const salary = (job.salary_min || job.salary_max) ? `$${job.salary_min || '—'}–$${job.salary_max || '—'}/hr` : null;
  return (
    <PanelCard title={t('interview.jobDetailsTitle')}>
      <div style={{ fontSize: '0.95rem', fontWeight: 700, color: WHITE, marginBottom: '8px' }}>{job.job_title}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '4px' }}>
        {job.trade_category && <Pill>{job.trade_category}</Pill>}
        {job.job_type && <Pill>{job.job_type}</Pill>}
      </div>
      <InfoRow label={t('employer.dashboard.candidates.locationLabel')} value={[job.city, job.state].filter(Boolean).join(', ')} />
      <InfoRow label={t('employer.dashboard.candidates.payRateLabel')} value={salary} />
    </PanelCard>
  );
}

function CandidateSnapshotPanel({ workerInfo, tradesInfo, certifications }) {
  const { t } = useTranslation();
  if (!workerInfo) return null;
  const fullName = [workerInfo.first_name, workerInfo.last_name].filter(Boolean).join(' ');
  return (
    <PanelCard title={t('interview.candidateSnapshotTitle')}>
      <div style={{ fontSize: '0.95rem', fontWeight: 700, color: WHITE, marginBottom: '8px' }}>{fullName}</div>
      <InfoRow label={t('worker.dashboard.myProfile.tradeLabel')} value={tradesInfo?.trade} />
      <InfoRow label={t('worker.signup.step5.yearsExperienceLabel')} value={tradesInfo?.years_experience} />
      <InfoRow label={t('worker.dashboard.myProfile.availabilityLabel')} value={tradesInfo?.availability} />

      {certifications.length > 0 && (
        <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <p style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('worker.dashboard.completionItems.certificationsItem')}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {certifications.map((cert, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.82rem', color: WHITE, fontWeight: 600 }}>{cert.certification_name}</span>
                {cert.verification_status === 'verified' && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', backgroundColor: 'rgba(29,158,117,0.15)', color: GREEN, borderRadius: '999px', fontSize: '0.66rem', fontWeight: 700, padding: '2px 8px', whiteSpace: 'nowrap' }}>
                    <LineIcon name="check" size={16} />
                    {t('interview.verifiedBadge')}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </PanelCard>
  );
}

function TranscriptPanel({ lines, unavailable, title, waitingMessage, unavailableMessage }) {
  const { t } = useTranslation();
  const scrollRef = useRef(null);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [lines]);

  return (
    <PanelCard title={title || t('interview.liveTranscriptTitle')}>
      <div ref={scrollRef} style={{ maxHeight: '260px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {unavailable ? (
          <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', margin: 0 }}>
            {unavailableMessage || t('interview.transcriptUnavailable')}
          </p>
        ) : lines.length === 0 ? (
          <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.35)', margin: 0 }}>
            {waitingMessage || t('interview.transcriptWaiting')}
          </p>
        ) : (
          lines.map(line => (
            <div key={line.id}>
              {line.speaker && <span style={{ fontSize: '0.72rem', fontWeight: 700, color: AMBER }}>{line.speaker}: </span>}
              <span style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.85)' }}>{line.text}</span>
            </div>
          ))
        )}
      </div>
    </PanelCard>
  );
}

// Daily's stored transcripts are plain WebVTT -- parsed defensively since the exact
// speaker-tagging convention (a <v Name> voice tag vs. untagged cue text) isn't confirmed;
// untagged cues just render without a speaker prefix rather than guessing at one.
function parseWebVTT(vttText) {
  if (!vttText) return [];
  const blocks = vttText.replace(/\r/g, '').split(/\n\n+/);
  const lines = [];
  blocks.forEach((block, i) => {
    const blockLines = block.split('\n').filter(Boolean);
    const timingIdx = blockLines.findIndex(l => l.includes('-->'));
    if (timingIdx === -1) return; // WEBVTT header, NOTE blocks, etc.
    const rawText = blockLines.slice(timingIdx + 1).join(' ').trim();
    if (!rawText) return;
    const voiceMatch = rawText.match(/^<v\s+([^>]+)>([\s\S]*?)(?:<\/v>)?$/i);
    const speaker = voiceMatch ? voiceMatch[1].trim() : '';
    const text = (voiceMatch ? voiceMatch[2] : rawText).replace(/<[^>]+>/g, '').trim();
    if (!text) return;
    lines.push({ id: `vtt-${i}`, speaker, text });
  });
  return lines;
}

async function fetchPersistedTranscript(interviewId) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) return null;
  try {
    const res = await fetch('/api/get-interview-transcript', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ interviewId }),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

// Persistent notes panel -- lives inline in the side panel (desktop) / drawer
// (mobile) alongside Job Details and Candidate Snapshot, instead of a modal
// that pops up over the video. Employer can jot notes at any point during
// or after the call; nothing here interrupts the call view.
function NotesPanel({ applicationId, initialNote, myUserId, myDisplayName }) {
  const { t } = useTranslation();
  const parsedInitial = parseEmployerNote(initialNote);
  const [tags, setTags]     = useState(parsedInitial.tags);
  const [text, setText]     = useState(parsedInitial.text);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const [authorName, setAuthorName] = useState(parsedInitial.authorName);

  const toggleTag = (tag) => {
    setTags(prev => (prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    const combined = serializeEmployerNote({ tags, origin: 'interview', addedAt: new Date().toISOString(), text, authorId: myUserId, authorName: myDisplayName });
    await supabase.from('applications').update({ employer_notes: combined }).eq('id', applicationId);
    setAuthorName(myDisplayName);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div id="interview-notes-panel">
      <PanelCard title={t('interview.interviewNotesTitle')}>
        <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', marginTop: '-6px', marginBottom: '10px' }}>
          {t('interview.notesPrivacyNote')}
        </p>
        {authorName && (
          <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', marginBottom: '10px' }}>
            {t('employer.dashboard.candidates.noteLastEditedBy', { name: authorName })}
          </div>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
          {QUICK_NOTE_TAGS.map(({ value, labelKey }) => {
            const active = tags.includes(value);
            const sentiment = tagSentiment(value);
            const color = sentiment === 'negative' ? RED : sentiment === 'positive' ? GREEN : AMBER;
            return (
              <button
                key={value}
                type="button"
                onClick={() => toggleTag(value)}
                style={{
                  background: active ? `${color}29` : 'rgba(255,255,255,0.06)',
                  border: `1px solid ${active ? color : 'rgba(255,255,255,0.18)'}`,
                  color: active ? color : 'rgba(255,255,255,0.55)',
                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                  fontSize: '0.74rem', fontWeight: 600, padding: '5px 10px',
                  borderRadius: '999px', cursor: 'pointer', fontFamily: FONT_BODY,
                }}
              >
                {active ? <LineIcon name="check" size={16} /> : '+ '}{t(labelKey)}
              </button>
            );
          })}
        </div>

        <textarea
          value={text}
          onChange={e => { setText(e.target.value); setSaved(false); }}
          placeholder={t('interview.notesPlaceholder')}
          rows={4}
          style={{
            width: '100%', padding: '10px 12px', fontSize: '0.85rem',
            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: '8px', color: WHITE, fontFamily: FONT_BODY,
            resize: 'vertical', outline: 'none', lineHeight: 1.5, boxSizing: 'border-box',
          }}
        />

        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px', marginTop: '10px' }}>
          {saved && <span style={{ fontSize: '0.75rem', color: GREEN, fontWeight: 600 }}>{t('interview.notesSavedLabel')}</span>}
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '9px 16px', borderRadius: '8px', border: 'none',
              backgroundColor: AMBER, color: NAVY, fontFamily: FONT_BODY,
              fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? t('common.saving') : t('interview.saveNotesButton')}
          </button>
        </div>
      </PanelCard>
    </div>
  );
}

export default function InterviewPage() {
  const { t, i18n } = useTranslation();
  const params        = new URLSearchParams(window.location.search);
  const interviewId   = params.get('interviewId') || '';
  const legacyRoomUrl = params.get('roomUrl') || '';
  const legacyJobTitle = params.get('jobTitle') || '';
  const legacyCompany = params.get('companyName') || '';
  // "View transcript" links add view=transcript: a Daily call can be held before interview_at,
  // so its transcript has to be reachable before the scheduled time too.
  const transcriptViewRequested = params.get('view') === 'transcript';

  const [left, setLeft]           = useState(false);
  const [iframeKey, setIframeKey] = useState(0);
  const iframeRef    = useRef(null);
  const callFrameRef = useRef(null);

  const [loading, setLoading]         = useState(!!interviewId);
  const [loadError, setLoadError]     = useState(false);
  const [interview, setInterview]     = useState(null);
  const [job, setJob]                 = useState(null);
  const [workerInfo, setWorkerInfo]   = useState(null);
  const [tradesInfo, setTradesInfo]   = useState(null);
  const [certifications, setCertifications] = useState([]);
  const [viewerRole, setViewerRole]   = useState(null); // 'employer' | 'union' | 'worker' | null
  const [myId, setMyId]               = useState(null);
  const [myDisplayName, setMyDisplayName] = useState('');
  const [companyLabel, setCompanyLabel] = useState(legacyCompany);
  const [applicationId, setApplicationId] = useState(null);
  const [existingNote, setExistingNote]   = useState('');
  const [shortlisted, setShortlisted]     = useState(false);
  const [shortlisting, setShortlisting]   = useState(false);

  const [activeSpeakerName, setActiveSpeakerName] = useState(null);
  const [transcriptLines, setTranscriptLines]     = useState([]);
  const [transcriptionUnavailable, setTranscriptionUnavailable] = useState(false);

  // Past-interview (no live call) state: read-only stored transcript instead of live captions.
  const [persistedTranscriptStatus, setPersistedTranscriptStatus] = useState(null); // null | 'pending' | 'ready' | 'unavailable'
  const [persistedTranscriptLines, setPersistedTranscriptLines]   = useState([]);
  const [showMobileDrawer, setShowMobileDrawer] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 860);

  useEffect(() => {
    document.title = t('interview.pageTitle');
    if (!document.querySelector('link[data-vw-fonts]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.setAttribute('data-vw-fonts', '1');
      link.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 860);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── Load interview context: job, candidate snapshot, viewer role, application ──
  useEffect(() => {
    if (!interviewId) { setLoading(false); return; }
    let cancelled = false;

    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const sessionUserId = session?.user?.id || null;
      setMyId(sessionUserId);

      const { data: iv } = await supabase.from('interviews').select('*').eq('id', interviewId).single();
      if (cancelled) return;
      if (!iv) { setLoadError(true); setLoading(false); return; }
      setInterview(iv);

      let role = null;
      if (sessionUserId && iv.worker_id === sessionUserId) role = 'worker';
      else if (sessionUserId && iv.union_id) {
        // Any active union_staff seat is "the union" here, not just the original signup.
        const { data: isUnionStaff } = await supabase.rpc('is_union_staff', { p_union_id: iv.union_id });
        if (isUnionStaff) role = 'union';
      }
      else if (sessionUserId && iv.employer_id) {
        // Not just "is this uid literally the employer" -- a genuine employer_staff seat
        // (a different auth.uid() from the employer that scheduled the interview) is just
        // as much "the employer" here as the original owner.
        const { data: isStaff } = await supabase.rpc('is_employer_staff', { p_employer_id: iv.employer_id });
        if (isStaff) role = 'employer';
      }
      if (cancelled) return;
      setViewerRole(role);

      if (role === 'employer' && iv.employer_id) {
        const { data: myStaffRow } = await supabase
          .from('employer_staff')
          .select('display_name')
          .eq('employer_id', iv.employer_id)
          .eq('user_id', sessionUserId)
          .maybeSingle();
        if (!cancelled) setMyDisplayName(myStaffRow?.display_name || '');
      }

      const lookups = [
        supabase.from('workers').select('first_name, last_name').eq('id', iv.worker_id).single(),
        supabase.from('worker_trades').select('trade, years_experience, availability').eq('worker_id', iv.worker_id).maybeSingle(),
        supabase.from('worker_certifications').select('certification_name, verification_status').eq('worker_id', iv.worker_id),
      ];
      if (iv.job_id) {
        lookups.push(supabase.from('jobs').select('job_title, trade_category, job_type, city, state, salary_min, salary_max').eq('id', iv.job_id).single());
      }
      if (iv.employer_id) {
        lookups.push(supabase.from('employers').select('company_name').eq('id', iv.employer_id).single());
      } else if (iv.union_id) {
        lookups.push(supabase.from('union_accounts').select('union_name').eq('id', iv.union_id).single());
      }

      const results = await Promise.all(lookups);
      if (cancelled) return;
      const [workerRes, tradesRes, certsRes, ...rest] = results;
      setWorkerInfo(workerRes.data || null);
      setTradesInfo(tradesRes.data || null);
      setCertifications(certsRes.data || []);

      let idx = 0;
      if (iv.job_id) { setJob(rest[idx]?.data || null); idx += 1; }
      if (iv.employer_id) { setCompanyLabel(rest[idx]?.data?.company_name || ''); }
      else if (iv.union_id) { setCompanyLabel(rest[idx]?.data?.union_name || ''); }

      // Shortlist / notes only make sense for the employer side of an actual job application.
      if (role === 'employer' && iv.job_id && iv.employer_id) {
        const { data: appRow } = await supabase
          .from('applications')
          .select('id, employer_notes, shortlisted_at')
          .eq('job_id', iv.job_id)
          .eq('worker_id', iv.worker_id)
          .eq('employer_id', iv.employer_id)
          .maybeSingle();
        if (appRow) {
          setApplicationId(appRow.id);
          setExistingNote(appRow.employer_notes || '');
          setShortlisted(!!appRow.shortlisted_at);
        }
      }

      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [interviewId]);

  const isPastInterview = !!interview && new Date(interview.interview_at) < new Date();
  const showTranscriptView = !!interview && (isPastInterview || (transcriptViewRequested && interview.meeting_type === 'daily'));

  // ── Past interview (or an explicit transcript view): no live call to join, just fetch and
  // render whatever transcript exists (poll-based -- see get-interview-transcript.js -- so this
  // is also the "opportunistic re-check while pending" trigger for whoever opens this page next).
  useEffect(() => {
    if (loading || !showTranscriptView || !interview || interview.meeting_type !== 'daily') return;
    let cancelled = false;
    (async () => {
      const result = await fetchPersistedTranscript(interview.id);
      if (cancelled || !result) return;
      setPersistedTranscriptStatus(result.status);
      if (result.status === 'ready' && result.vtt) {
        setPersistedTranscriptLines(parseWebVTT(result.vtt));
      }
    })();
    return () => { cancelled = true; };
  }, [loading, showTranscriptView, interview]); // eslint-disable-line

  // ── Daily.co: wrap the iframe (keeps the prebuilt call UI) so we can listen for
  // transcription / active-speaker events and drive our own panels off them ──────
  useEffect(() => {
    if (loading || showTranscriptView) return;
    const meetingLink = interview?.meeting_link || legacyRoomUrl;
    if (!meetingLink || !iframeRef.current) return;

    const myDisplayName = viewerRole === 'worker'
      ? [workerInfo?.first_name, workerInfo?.last_name].filter(Boolean).join(' ')
      : (companyLabel || legacyCompany || undefined);

    const callFrame = DailyIframe.wrap(iframeRef.current, {
      showLeaveButton: false,
      showFullscreenButton: false,
      iframeStyle: { width: '100%', height: '100%', border: 'none' },
    });
    callFrameRef.current = callFrame;

    const joinUrl = meetingLink + (meetingLink.includes('?') ? '&' : '?') + 'dark=1';

    const handleTranscriptionMessage = (ev) => {
      if (ev.is_final === false || !ev.text) return;
      const participants = callFrame.participants ? callFrame.participants() : {};
      const p = participants?.[ev.participantId] || participants?.[ev.session_id];
      const speaker = ev.user_name || p?.user_name || (p?.local ? t('interview.youLabel') : t('interview.participantLabel'));
      setTranscriptLines(prev => [...prev, { id: `${ev.timestamp || Date.now()}-${prev.length}`, speaker, text: ev.text }].slice(-300));
    };
    const handleTranscriptionError = () => setTranscriptionUnavailable(true);
    const handleActiveSpeakerChange = (ev) => {
      const peerId = ev?.activeSpeaker?.peerId;
      if (!peerId) { setActiveSpeakerName(null); return; }
      const participants = callFrame.participants ? callFrame.participants() : {};
      const p = participants?.[peerId];
      setActiveSpeakerName(p ? (p.local ? t('interview.youLabel') : (p.user_name || t('interview.participantLabel'))) : null);
    };
    const handleLeftMeeting = () => {
      setLeft(true);
      // Kick off the first transcript check right as the call ends (null -> pending, or
      // straight to ready/unavailable) -- fire-and-forget, this page isn't showing it live.
      if (interviewId) fetchPersistedTranscript(interviewId);
    };

    callFrame.on('transcription-message', handleTranscriptionMessage);
    callFrame.on('transcription-error', handleTranscriptionError);
    callFrame.on('active-speaker-change', handleActiveSpeakerChange);
    callFrame.on('left-meeting', handleLeftMeeting);

    callFrame
      .join({ url: joinUrl, userName: myDisplayName || undefined })
      .then(() => {
        // startTranscription() doesn't return a thenable in this daily-js version -- real
        // success/failure is reported via the transcription-message/transcription-error
        // events above, not this call's return value. Only guard against it throwing
        // synchronously (e.g. called in an invalid call state).
        try {
          callFrame.startTranscription?.();
        } catch (err) {
          setTranscriptionUnavailable(true);
        }
      })
      .catch(err => {
        console.error('[Interview] Failed to join call:', err);
      });

    return () => {
      callFrame.off('transcription-message', handleTranscriptionMessage);
      callFrame.off('transcription-error', handleTranscriptionError);
      callFrame.off('active-speaker-change', handleActiveSpeakerChange);
      callFrame.off('left-meeting', handleLeftMeeting);
      callFrame.destroy();
      callFrameRef.current = null;
    };
  }, [loading, iframeKey]); // eslint-disable-line

  const handleLeaveClick = () => {
    callFrameRef.current?.leave();
    setLeft(true);
  };

  // Notes live in an always-mounted panel now (side panel on desktop, inside
  // the drawer on mobile) instead of a modal, so there's nothing to "open" --
  // this just brings it into view for the post-leave "Add notes" affordance.
  const scrollToNotes = () => {
    if (isMobile) {
      setShowMobileDrawer(true);
      setTimeout(() => {
        document.getElementById('interview-notes-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    } else {
      document.getElementById('interview-notes-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleRejoin = () => {
    setLeft(false);
    setTranscriptLines([]);
    setActiveSpeakerName(null);
    setTranscriptionUnavailable(false);
    setIframeKey(k => k + 1);
  };

  const handleShortlist = async () => {
    if (!applicationId || shortlisted || shortlisting) return;
    setShortlisting(true);
    const { error } = await supabase.from('applications').update({ shortlisted_at: new Date().toISOString() }).eq('id', applicationId);
    setShortlisting(false);
    if (error) { alert(t('interview.shortlistErrorPrefix') + error.message); return; }
    setShortlisted(true);
  };

  const jobTitleLabel = job?.job_title || legacyJobTitle;
  const centerLabel = [jobTitleLabel, companyLabel].filter(Boolean).join(' · ');
  const showCandidatePanels = viewerRole === 'employer' || viewerRole === 'union';

  const panels = (
    <>
      <JobDetailsPanel job={job} />
      {showCandidatePanels && (
        <CandidateSnapshotPanel
          workerInfo={workerInfo}
          tradesInfo={tradesInfo}
          certifications={certifications}
        />
      )}
      {showCandidatePanels && applicationId && (
        <NotesPanel applicationId={applicationId} initialNote={existingNote} myUserId={myId} myDisplayName={myDisplayName} />
      )}
      <TranscriptPanel lines={transcriptLines} unavailable={transcriptionUnavailable} />
    </>
  );

  // ── Past interview: read-only summary + transcript, no video/join UI at all -- the
  // call already happened, so there's nothing live to connect to. ───────────────────
  if (!loading && showTranscriptView) {
    const transcriptUnavailable = persistedTranscriptStatus === 'unavailable' || persistedTranscriptStatus === null;
    return (
      <div style={{ minHeight: '100dvh', backgroundColor: NAVY, fontFamily: FONT_BODY }}>
        <div style={{
          height: 60, paddingTop: 'env(safe-area-inset-top)', paddingLeft: '16px', paddingRight: '16px',
          backgroundColor: '#1a2a3a', borderBottom: `1px solid ${AMBER}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
          boxSizing: 'content-box',
        }}>
          <span style={{ fontFamily: FONT_HEAD, color: WHITE, fontSize: isMobile ? '16px' : '20px', letterSpacing: '2px' }}>
            VOCATION WORKS
          </span>
          <LanguageToggle userId={myId} />
        </div>

        <div style={{ maxWidth: 640, margin: '0 auto', padding: '28px 16px 48px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ textAlign: 'center', marginBottom: '6px' }}>
            <p style={{ fontFamily: FONT_HEAD, color: WHITE, fontSize: '24px', letterSpacing: '1px', margin: '0 0 4px' }}>
              {t('interview.interviewEndedHeading')}
            </p>
            {centerLabel && <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', margin: 0 }}>{centerLabel}</p>}
          </div>

          {loadError ? (
            <p style={{ color: WHITE, fontSize: '15px', textAlign: 'center' }}>{t('interview.loadFailedMessage')}</p>
          ) : (
            <>
              <JobDetailsPanel job={job} />
              {showCandidatePanels && (
                <CandidateSnapshotPanel workerInfo={workerInfo} tradesInfo={tradesInfo} certifications={certifications} />
              )}
              {showCandidatePanels && applicationId && (
                <NotesPanel applicationId={applicationId} initialNote={existingNote} myUserId={myId} myDisplayName={myDisplayName} />
              )}
              {interview.meeting_type === 'daily' && (
                <TranscriptPanel
                  lines={persistedTranscriptLines}
                  unavailable={transcriptUnavailable}
                  title={t('interview.transcriptTitle')}
                  waitingMessage={t('interview.transcriptProcessingMessage')}
                  unavailableMessage={t('interview.transcriptNotAvailableMessage')}
                />
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  const hasPanelContent = !!job || showCandidatePanels;

  return (
    <div style={{
      width: '100vw', height: '100dvh', overflow: 'hidden',
      backgroundColor: NAVY,
      display: 'flex', flexDirection: 'column',
      fontFamily: FONT_BODY,
    }}>
      {/* Header */}
      <div style={{
        height: 60, flexShrink: 0,
        paddingTop: 'env(safe-area-inset-top)', paddingLeft: '16px', paddingRight: '16px',
        backgroundColor: '#1a2a3a',
        borderBottom: `1px solid ${AMBER}`,
        display: 'flex', alignItems: 'center',
        gap: '12px',
        boxSizing: 'content-box',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
          {/* The wordmark and section label are dropped below ~860px -- at phone widths,
              keeping them left no room for the Live badge / Leave button on the right, pushing
              those safety-critical controls off-screen since this row's sections don't shrink.
              So the lockup shrinks to the mark alone there rather than disappearing. */}
          <VocationWorksLogo variant="reversed" layout={isMobile ? 'mark' : 'horizontal'} size={isMobile ? 32 : 30} />
          {!isMobile && (
            <>
              <div style={{ width: 1, height: 20, backgroundColor: AMBER, opacity: 0.5, flexShrink: 0 }} />
              <span style={{ fontFamily: FONT_HEAD, color: AMBER, fontSize: '18px', letterSpacing: '0.06em' }}>
                {t('interview.videoInterviewSectionLabel')}
              </span>
            </>
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden', padding: '0 8px' }}>
          {centerLabel && (
            <span style={{ color: WHITE, fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {centerLabel}
            </span>
          )}
        </div>

        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '10px' }}>
          <LanguageToggle userId={myId} />
          {applicationId && (
            <button
              onClick={handleShortlist}
              disabled={shortlisted || shortlisting}
              title={shortlisted ? t('interview.shortlistedTooltip') : t('interview.shortlistTooltip')}
              aria-label={isMobile ? (shortlisted ? t('interview.shortlistedLabel') : t('interview.shortlistLabel')) : undefined}
              aria-pressed={shortlisted}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                backgroundColor: shortlisted ? 'rgba(29,158,117,0.15)' : AMBER,
                color: shortlisted ? GREEN : NAVY,
                border: shortlisted ? `1px solid ${GREEN}` : 'none',
                borderRadius: isMobile ? '50%' : '20px', fontSize: '12px', fontWeight: 700,
                ...(isMobile ? { width: '44px', height: '44px', padding: 0 } : { padding: '6px 14px' }),
                cursor: shortlisted ? 'default' : 'pointer', whiteSpace: 'nowrap',
                opacity: shortlisting ? 0.7 : 1,
              }}
            >
              {shortlisting && isMobile ? '…' : <LineIcon name="star" size={isMobile ? 20 : 16} filled={shortlisted} color={shortlisted ? AMBER : 'currentColor'} />}
              {!isMobile && (shortlisted ? t('interview.shortlistedLabel') : shortlisting ? t('interview.shortlistingLabel') : t('interview.shortlistLabel'))}
            </button>
          )}
          <div style={{
            backgroundColor: '#EF4444', color: WHITE,
            borderRadius: '20px', padding: isMobile ? '4px' : '4px 10px',
            fontSize: '12px', fontWeight: 700,
            display: 'flex', alignItems: 'center', gap: '5px',
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: WHITE, display: 'inline-block' }} />
            {!isMobile && t('interview.liveLabel')}
          </div>
          <button
            onClick={handleLeaveClick}
            style={{
              backgroundColor: '#EF4444', color: WHITE,
              border: 'none', borderRadius: '20px',
              padding: isMobile ? '6px 10px' : '6px 14px', fontSize: '12px', fontWeight: 700,
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            {isMobile ? t('interview.leaveButtonMobile') : t('interview.leaveInterviewButton')}
          </button>
        </div>
      </div>

      {/* Main area: video + side panel (desktop) / stacked + drawer (mobile) */}
      <div style={{ flex: 1, display: 'flex', flexDirection: isMobile ? 'column' : 'row', overflow: 'hidden' }}>
        {/* Video area */}
        <div style={{ flex: 1, position: 'relative', overflow: 'hidden', minHeight: 0 }}>
          {/* A plain div (not a replaced element like <iframe>) is needed to actually stretch
              between top and bottom via absolute positioning -- an <iframe> with height:'auto'
              and top+bottom both set does NOT fill the gap the way a normal block box does; it
              falls back to a small intrinsic default height regardless of viewport size, which
              in turn makes Daily's own responsive prejoin UI (rendered inside that undersized
              iframe) mis-layout at every screen width. This wrapper carries the safe-area inset,
              and the iframe just fills 100% of it. */}
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 'env(safe-area-inset-bottom)' }}>
            <iframe
              key={iframeKey}
              ref={iframeRef}
              style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
              allow="camera; microphone; fullscreen; display-capture; autoplay"
              title={t('interview.videoInterviewIframeTitle')}
            />
          </div>

          {activeSpeakerName && !left && (
            <div style={{
              position: 'absolute', top: 14, left: 14, zIndex: 5,
              display: 'flex', alignItems: 'center', gap: '8px',
              backgroundColor: 'rgba(13,27,42,0.85)', border: `1.5px solid ${AMBER}`,
              borderRadius: '999px', padding: '6px 14px',
              boxShadow: '0 0 0 3px rgba(245,166,35,0.15)',
            }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: AMBER, display: 'inline-block' }} />
              <span style={{ fontSize: '12px', fontWeight: 700, color: WHITE }}>{t('interview.speakingIndicator', { name: activeSpeakerName })}</span>
            </div>
          )}

          {isMobile && hasPanelContent && !left && (
            <button
              onClick={() => setShowMobileDrawer(true)}
              style={{
                position: 'absolute', bottom: 'calc(14px + env(safe-area-inset-bottom))', left: '50%', transform: 'translateX(-50%)', zIndex: 5,
                backgroundColor: AMBER, color: NAVY, border: 'none', borderRadius: '999px',
                padding: '9px 18px', fontSize: '12px', fontWeight: 700, cursor: 'pointer',
              }}
            >
              {t('interview.detailsButtonMobile')}
            </button>
          )}

          {loadError && (
            <div style={{ position: 'absolute', inset: 0, zIndex: 6, backgroundColor: 'rgba(13,27,42,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <p style={{ color: WHITE, fontSize: '15px' }}>{t('interview.loadFailedMessage')}</p>
            </div>
          )}

          {left && (
            <div style={{
              position: 'absolute', inset: 0, zIndex: 10,
              backgroundColor: 'rgba(13,27,42,0.92)',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              gap: '24px',
            }}>
              <p style={{
                fontFamily: FONT_HEAD,
                color: WHITE, fontSize: '28px', letterSpacing: '2px',
                margin: 0, textAlign: 'center',
              }}>
                {t('interview.leftInterviewMessage')}
              </p>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <button
                  onClick={handleRejoin}
                  style={{
                    backgroundColor: AMBER, color: NAVY,
                    border: 'none', borderRadius: '24px',
                    padding: '12px 28px', fontSize: '15px', fontWeight: 700,
                    cursor: 'pointer', letterSpacing: '0.02em',
                  }}
                >
                  {t('interview.rejoinButton')}
                </button>
                {applicationId && (
                  <button
                    onClick={scrollToNotes}
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.12)', color: WHITE,
                      border: 'none', borderRadius: '24px',
                      padding: '12px 24px', fontSize: '14px', fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {t('interview.addNotesButton')}
                  </button>
                )}
                <button
                  onClick={() => window.close()}
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)',
                    border: 'none', borderRadius: '24px',
                    padding: '12px 24px', fontSize: '14px', fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {t('interview.closeTabButton')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Side panel: desktop only, sticky */}
        {!isMobile && hasPanelContent && (
          <div style={{
            width: 360, flexShrink: 0, overflowY: 'auto',
            borderLeft: '1px solid rgba(255,255,255,0.08)',
            padding: '16px', display: 'flex', flexDirection: 'column', gap: '14px',
          }}>
            {panels}
          </div>
        )}
      </div>

      {/* Mobile drawer */}
      {isMobile && showMobileDrawer && (
        <div
          onClick={e => { if (e.target === e.currentTarget) setShowMobileDrawer(false); }}
          style={{ position: 'fixed', inset: 0, zIndex: 50, backgroundColor: 'rgba(0,0,0,0.6)' }}
        >
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, maxHeight: '78dvh',
            overflowY: 'auto', backgroundColor: NAVY,
            borderTopLeftRadius: '18px', borderTopRightRadius: '18px',
            paddingTop: '16px', paddingLeft: '16px', paddingRight: '16px',
            paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
            display: 'flex', flexDirection: 'column', gap: '14px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowMobileDrawer(false)}
                style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: '20px', cursor: 'pointer', lineHeight: 1 }}
              >
                ×
              </button>
            </div>
            {panels}
          </div>
        </div>
      )}
    </div>
  );
}
